<script lang="ts" setup>
    import { ref, watch, computed } from "vue";
    import { ISearchBaseColumnItem, ISearchBaseInputs } from "../../lib/Models/SearchModel";
    import { ChoiceUtils } from "../../LibMain";

    const emit = defineEmits(["performSearch", "onChange"]);

    const props = withDefaults(defineProps<{
        // Placeholder text to be displayed inside text control if no values
        placeholder: string,
        // Label for the component
        label: string,
        // Column element name from the result set to be displayed in the text control when search item selected
        displayColumn: string,
        // Columns to be displayed from the result set when search is performed 
        columns: Array<ISearchBaseColumnItem>, 
        // Results from the search
        searchResults: Array<any>
        // The default search value 
        defaultSearchText?: string,
        // The maximum height of the search results
        maxHeight?: number,
        // Default error message
        defaultErrorMessage: string,
        // Dispaly Error Message
        displayErrorMessage: boolean
    }>(), {
        placeholder: "",
        label: "",
        displayColumn: "",
        defaultSearchText: "",
        maxHeight: 200,
        defaultErrorMessage: "",
        displayErrorMessage: false
    });

    const displayResults = ref<boolean>(false);
    const searchText = ref<string>("");
    const moreResults = ref<boolean>(false);
    const allResults = ref<boolean>(false);
    const processing = ref<boolean>(false);
    const displayError = ref<boolean>(false);

    watch(() => props.searchResults, (_new, _old) => {
        displayResults.value = true;
        moreResults.value = false;
        processing.value = false;
        if (!_new)
            return;
        if (_new.length == 1) {
            searchText.value = _new[0][props.displayColumn];
            displayResults.value = false;    
            emit("onChange", _new[0]);
        }
        if (_new.length >= 50 && !allResults.value) {
            moreResults.value = true;
        }          
    });

    watch(() => props.displayErrorMessage, (_new, _old) => {
        displayError.value = _new;
    });

    const searchButtonId = computed(()=>{
        return `${ChoiceUtils.randomId()}SearchButton`;
    });

    const searchTextId = computed(()=>{
        return `${ChoiceUtils.randomId()}SearchText`;
    });

    const displayLabel = computed(()=>{
        return props.label.length > 0 ? true : false;
    });

    const displayFooterResults = computed(()=>{
        if (props.searchResults) {
            if (props.searchResults.length > 0) {
                return !moreResults.value;
            }
        }
        return true;
    });

    const footerText = computed(()=>{
        if (props.searchResults) {
            if (props.searchResults.length > 0) {
                return `All Results Displayed (${props.searchResults.length})`;
            }
        }
        return "No Results Found";
    });

    const searchBorder = computed(()=>{
        if (typeof props.defaultErrorMessage === "string" && props.defaultErrorMessage.trim().length == 0)
            return "form-control";
        return displayError.value ? "form-control border-danger" : "form-control";
    });

    async function onSearch(displayAllResults: boolean) {
        allResults.value = displayAllResults;
        const searchInputs: ISearchBaseInputs = { searchText: searchText.value, displayAllResults: displayAllResults };
        processing.value = true;
        emit("performSearch", searchInputs);
    }

    function onSelect(selectedItem: any) {
        searchText.value = selectedItem[props.displayColumn];
        displayResults.value = false;
        displayError.value = false;
        emit("onChange", selectedItem);
    }

    function onCloseSearch() {
        displayResults.value = false;
    }
</script>

<template>
    <label v-if="displayLabel" :for="searchTextId">{{label}}</label>
    <div class="input-group">
        <input type="text" autocomplete="off" :class="searchBorder" :id="searchTextId" :placeholder="placeholder" :aria-label="placeholder" :aria-describedby="searchButtonId" v-model="searchText" v-on:keyup.enter="onSearch(false)">
        <button class="btn btn-primary" type="button" :id="searchButtonId" @click="onSearch(false)">
            Search
            <span v-show="processing" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </button>
    </div>
    <div v-if="displayError" class="text-danger">{{defaultErrorMessage}}</div>
    <div  v-if="displayResults" class="overflow-auto" :style="{maxHeight: maxHeight + 'px'}">
        <table class="table table-bordered table-hover small" aria-label="Patient Search Results">
            <thead>
                <tr>
                    <th v-for="col in columns">{{col.title}}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="result in searchResults" @click="onSelect(result)">
                    <td v-for="col in columns">{{result[col.element]}}</td>                    
                </tr>
            </tbody>
        </table>
    </div>
    <div v-if="displayResults" class="row align-items-center mt-2">
        <div class="col">
            <span class="text-muted" v-if="displayFooterResults">{{footerText}}</span>
            <button v-if="!displayFooterResults" type="button" class="btn btn-outline-primary btn-sm" @click="onSearch(true)">Display All Results</button>        
        </div>
        <div class="col-md-auto">
            <button type="button" class="btn btn-outline-primary btn-sm" @click="onCloseSearch()">Close Search</button>
        </div>
    </div>
</template>